import posthog from 'posthog-js'

/**
 * This app's name. All four applications report into ONE PostHog project — the
 * `organizations_projects` feature is licence-gated on the self-hosted instance
 * and a licence is not purchasable for it — so this is what keeps their people
 * and events apart. Registered as a super property, and prefixed onto the
 * distinct id, because `identify(String(user.id))` would otherwise make user 1
 * here and user 1 in a sibling app the same person.
 */
const APP_NAME = 'power'

/**
 * PostHog product analytics for the SPA.
 *
 * Sits alongside `lib/faro.js`: Faro answers "is the app healthy" (errors, Web
 * Vitals, traces), PostHog answers "what did people do" (pageviews, clicks,
 * funnels, flags, replay).
 *
 * `initPostHog()` is a no-op when `VITE_POSTHOG_KEY` is unset, so dev and any
 * existing build are untouched until the key is set. See
 * map_server/posthog-notes.md for the self-hosted instance.
 *
 * CAPTURE LEVEL: deliberately maximal while these sites are demos — autocapture
 * (including copied text), heatmaps, performance, dead clicks, exceptions and
 * session replay are all on, and `person_profiles: 'always'` means anonymous
 * visitors get profiles too.
 *
 * `maskAllInputs` stays at its default `true`, so replay never records what
 * anyone types. Before this app carries real user data, revisit:
 *   - `autocapture` sends the *text* of the element clicked, which can itself be
 *     identifying (a person's name in a row action). `mask_all_text: true` keeps
 *     the click structure without the text.
 *   - add a `ph-no-capture` class to any element that must never appear in a
 *     recording; it is replaced by a same-size block.
 *   - session replay records real sessions. Turn it off in PostHog project
 *     settings, or set `disable_session_recording: true` here.
 *
 * Note that replay, heatmaps and exception capture are ALSO project-level
 * toggles in PostHog — the options below do nothing until they are enabled there.
 */

let initialised = false

export function initPostHog() {
    const key = import.meta.env.VITE_POSTHOG_KEY
    if (!key || initialised) {
        return
    }

    try {
        posthog.init(key, {
            api_host:
                import.meta.env.VITE_POSTHOG_HOST ||
                'https://ph.rosslyoung.com',
            // Opt in to the current generation of SDK defaults rather than the
            // legacy ones kept for old installs. Every option below is set
            // explicitly anyway and overrides this.
            defaults: '2026-08-30',
            // Profiles for anonymous visitors too, not just identified ones.
            person_profiles: 'always',
            autocapture: {
                capture_copied_text: true
            },
            // Both routers navigate via history.pushState, which a plain
            // `true` would not see — only the initial load would be captured.
            capture_pageview: 'history_change',
            capture_pageleave: true,
            capture_performance: true,
            capture_heatmaps: true,
            capture_dead_clicks: true,
            capture_exceptions: true,
            disable_session_recording: false,
            enable_recording_console_log: true,
            session_recording: {
                // Default, stated explicitly: never record keystrokes.
                maskAllInputs: true
            }
        })
        // Super property: attached to every event from here on, including
        // autocaptured ones, so insights can be filtered to this app.
        posthog.register({ app: APP_NAME })
        initialised = true
    } catch (_err) {
        // PostHog failure must never break app boot.
    }
}

/**
 * Tie the current browser identity to a user, merging their anonymous history.
 *
 * This is what makes the same person on power.rosslyoung.com and apps.fettes.com
 * one person rather than two: `distinct_id` lives in per-origin storage, so the
 * two hosts start as separate anonymous visitors and only converge here.
 *
 * @param {{id: number|string, username?: string, email?: string}} user
 */
export function identifyUser(user) {
    if (!initialised || !user?.id) {
        return
    }
    try {
        posthog.identify(`${APP_NAME}:${user.id}`, {
            username: user.username,
            email: user.email
        })
    } catch (_err) {
        // Never throw — telemetry must not affect app behaviour.
    }
}

/**
 * Clear the identity on logout so the next visitor on this browser is not
 * attributed to the user who just left.
 */
export function resetPostHog() {
    if (!initialised) {
        return
    }
    try {
        posthog.reset()
    } catch (_err) {
        // Never throw.
    }
}

/**
 * Emit a custom product event. No-op when PostHog is not initialised
 * (`VITE_POSTHOG_KEY` unset), so call sites stay unconditional.
 *
 * @param {string} name - Event name, e.g. 'report_exported'
 * @param {Record<string, unknown>} [properties={}] - Event properties
 */
export function trackEvent(name, properties = {}) {
    if (!initialised) {
        return
    }
    try {
        posthog.capture(name, properties)
    } catch (_err) {
        // Never throw.
    }
}

export default posthog
